/**
 * useRecording Hook
 *
 * Provides MediaRecorder-based video recording.
 * Fresh implementation for SLAM S&C Movement Analysis (Phase 3).
 */
import { useState, useRef, useCallback, useEffect } from 'react';

const isRecordingSupported = typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && typeof MediaRecorder !== 'undefined';

export function useRecording() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [duration, setDuration] = useState(0);
  const timerRef = useRef(null);

  // Cleanup timer on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = useCallback((stream, mimeType = 'video/webm') => {
    chunksRef.current = [];
    setRecordedChunks([]);
    setDuration(0);
    setRecordedBlob(null);

    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
        setRecordedChunks([...chunksRef.current]);
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordedBlob(blob);
      clearInterval(timerRef.current);
    };
    mediaRecorderRef.current = recorder;
    recorder.start(1000); // 1s chunks
    setIsRecording(true);
    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(timerRef.current);
  }, []);

  const clearRecording = useCallback(() => {
    setRecordedBlob(null);
    setRecordedChunks([]);
    chunksRef.current = [];
    setDuration(0);
  }, []);

  return {
    isRecording,
    isRecordingSupported,
    recordedBlob,
    recordedChunks,
    duration,
    mediaRecorderRef,
    startRecording,
    stopRecording,
    clearRecording,
  };
}
